<template>
  <template v-if='isSent'>
    <div
      class='b-input-v4 code-area'
      :class='{ "field_with_errors": isInvalidCode }'
    >
      <input
        v-for='index in DIGITS'
        :key='index'
        ref='confirmationCodeRefs'
        type='number'
        inputmode='numeric'
        pattern='[0-9]'
        :autocomplete='index === 1 ? "one-time-code" : null'
        min='0'
        max='9'
        @input='onConfirmationCodeInput'
        @keydown='onKeyDown'
        @focus='onInputFocus'
        @paste='index === 1 ? onPaste($event) : null'
      >
    </div>
    <p v-if='isInvalidCode' class='error-message'>
      {{ tp('invalid_confirmation_code') }}
    </p>

    <p v-if='!isResend && (!isValid || isInvalidCode)'>
      <template v-if='!isInvalidCode'>
        {{ tp('resend_hint') }}
        <br>
      </template>
      {{ tp('resend_in') }} {{ resendInSeconds }} {{ tp('seconds', { count: resendInSeconds }) }}
    </p>

    <button
      v-if='isResend && (!isValid || isInvalidCode)'
      class='resend-confirmation'
      :disabled='!phoneNumber || isXhr'
      @click='sendConfirmation'
    >
      {{ tp('resend') }}
    </button>
  </template>

  <!-- v-if='!isSent || (isResend && (!isValid || isInvalidCode))' -->
  <template v-else>
    <button
      class='send-confirmation'
      :disabled='!phoneNumber || isXhr'
      @click='sendConfirmation'
    >
      {{ tp('confirm') }}
    </button>
  </template>
</template>

<script>
export default { name: 'CodeConfirmation' };
</script>

<script setup>
import { ref, computed, nextTick, onBeforeUnmount } from 'vue';
import delay from 'delay';

import { prepared } from '@/helpers/i18n';
import axios from '@/helpers/axios';

const props = defineProps({
  phoneNumber: { type: String, required: false, default: undefined },
  confirmationUrl: { type: String, required: true },
  confirmationType: { type: String, required: true },
  resendCodeInterval: { type: Number, required: true },
  isInvalidCode: { type: Boolean, required: true },
  isPrivacyPolicy: { type: Boolean, required: true }
});

const tp = prepared('frontend.phone_area.code_confirmation');
const emit = defineEmits(['confirmation_code:sent']);

const DIGITS = 4;

const isPrivacyPolicyAccepted = defineModel('isPrivacyPolicyAccepted', { type: Boolean });
const isPrivacyPolicyError = defineModel('isPrivacyPolicyError', { type: Boolean });

const confirmationCodeRefs = ref(null);
const confirmationCode = ref('');

const resendInSeconds = ref(props.resendCodeInterval);
const resendTimer = ref(null);

const isResend = ref(false);
const isXhr = ref(false);
const isSent = ref(false);

async function sendConfirmation(e) {
  e.preventDefault();
  e.stopImmediatePropagation();

  if (props.isPrivacyPolicy && !isPrivacyPolicyAccepted.value) {
    isPrivacyPolicyError.value = true;
    return;
  }
  emit('confirmation_code:sent');

  isXhr.value = true;
  const { data } = await axios.post(
    props.confirmationUrl,
    { phone: props.phoneNumber, kind: props.confirmationType }
  );

  isXhr.value = false;
  isSent.value = true;
  isResend.value = false;

  restartResendTimer();

  await nextTick();
  clearConfirmationCode();
  confirmationCodeRefs.value[0].focus();
};

function clearConfirmationCode() {
  if (!confirmationCodeRefs.value) { return; }

  confirmationCodeRefs.value?.forEach(node => node.value = '');
  onConfirmationCodeInput({ target: confirmationCodeRefs.value[0] });
}

function reset() {
  clearConfirmationCode();

  isSent.value = false;
  isResend.value = false;
  stopResendTimer();
};

const isValid = computed(() => (
  confirmationCode.value.trim().length === DIGITS
));

// handle regular input changes
function onConfirmationCodeInput({ target }) {
  if (!target) { return; }

  if (target.value.length > 1) {
    target.value = target.value[target.value.length - 1];
  }

  updateConfirmationCode();

  // auto-focus to the next input if needed
  const index = confirmationCodeRefs.value.indexOf(target);
  if (target.value && index < DIGITS - 1) {
    confirmationCodeRefs.value[index + 1].focus();
  }
}

function onPaste(event) {
  event.preventDefault();

  const pastedText = (event.clipboardData || window.clipboardData).getData('text');
  const digits = pastedText.replace(/\D/g, '').slice(0, DIGITS);

  if (digits.length > 0) {
    digits.split('').forEach((digit, index) => {
      if (index < DIGITS && confirmationCodeRefs.value[index]) {
        confirmationCodeRefs.value[index].value = digit;
      }
    });

    updateConfirmationCode();

    const focusIndex = Math.min(digits.length, DIGITS - 1);
    confirmationCodeRefs.value[focusIndex].focus();
  }
}

// keyboard specific events for desktop
function onKeyDown(event) {
  const { key, target } = event;
  const index = confirmationCodeRefs.value.indexOf(target);

  if (key === 'Backspace') {
    if (target.value) {
      target.value = '';
      updateConfirmationCode();
    } else {
      confirmationCodeRefs.value[index - 1]?.focus();
    }
  } else if (key === 'ArrowLeft') {
    confirmationCodeRefs.value[index - 1]?.focus();
  } else if (key === 'ArrowRight') {
    confirmationCodeRefs.value[index + 1]?.focus();
  }
}

function updateConfirmationCode() {
  confirmationCode.value = confirmationCodeRefs.value.map(node => node.value).join('');
}

// select all text when focusing on an input
async function onInputFocus({ target }) {
  await delay();
  target.select();
}

function restartResendTimer() {
  stopResendTimer();

  resendInSeconds.value = props.resendCodeInterval;
  resendTimer.value = setInterval(processResendInterval, 1000);
}

function stopResendTimer() {
  if (!resendTimer.value) { return; }

  clearInterval(resendTimer.value);
  resendTimer.value = null;
}

function processResendInterval() {
  resendInSeconds.value -= 1;

  if (resendInSeconds.value === 0) {
    stopResendTimer();
    isResend.value = true;
  }
}

onBeforeUnmount(stopResendTimer);

defineExpose({
  isValid,
  isSent,
  confirmationCode,
  reset
});
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/button_v4'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'

.send-confirmation
  +button_v4('primary', 'xl')
  width: 100%

.resend-confirmation
  +p4(500)
  color: var(--primary-color)
  text-align: center
  transition: color var(--transition-duration)
  width: 100%

  &:not(:disabled)
    cursor: pointer

  +gte_laptop
    &:hover
      color: var(--primary-color-hover)

  &:active
    color: var(--primary-color-active)

  &:disabled
    color: var(--primary-color-inactive)

.code-area
  display: flex
  justify-content: center

  +lte_ipad
    gap: rem4(12px)

  +gte_laptop
    gap: 12px

  input
    +p1(700)
    -moz-appearance: textfield
    padding: 0
    text-align: center

    +lte_ipad
      height: rem4(56px)
      width: rem4(56px)

    +gte_laptop
      height: 56px
      width: 56px

    &::-webkit-inner-spin-button,
    &::-webkit-outer-spin-button
      -webkit-appearance: none

p
  +p4(500)
  color: var(--gray-500)
  text-align: center

  .invalid-code
    color: var(--primary-color)
</style>
